import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { PeriodPicker, ponerPeriodo, periodoTexto } from '../components/PeriodPicker';
import { TopBar } from '../components/TopBar';
import { Pestanas } from '../components/Pestanas';
import { MasMenu } from '../components/MasMenu';
import { api } from '../lib/api';
import { confirmar, textoDeError } from '../components/Dialogo';
import { usePeriodoInicial } from '../lib/preferencias';
import { usePermisos } from '../lib/permisos';
import { descargarExcel, nombreArchivo, type Columna as ColExcel, type Celda } from '../lib/excel';
import {
  Campo, Filtro, Filtros, TIPO_TXT, columnasDeInicio, comparar, cumple, elementos, estadisticas, fechaDia, filtroActivo, filtroPara,
  filtroTexto, histograma, inferirCampos, numero, palabras, textoValor,
} from '../lib/formularios';

/**
 * Formularios web — lo que la gente llena en los sitios (solo admins).
 *
 * Fuentes vivas: safevity.com (el mini-formulario de franquicias, que se guarda paso a paso y además manda un correo
 * por Web3Forms), sandyduenas.com (los formularios del sitio) y el sitio de DTB (p. ej. la conferencia). Todo cae en la
 * base de Dueña de tu Belleza (tabla web_leads, aparte de los pacientes).
 *
 * "Incompleto" es dato, no basura: el formulario se va guardando conforme la persona avanza. Sin nombre ni teléfono =
 * empezó y no terminó. Se enseña con su estado.
 *
 * LAS RESPUESTAS SON COLUMNAS (antes eran pastillas amontonadas en una sola celda). Cada formulario es distinto, así que
 * nada aquí sabe de uno en particular: `lib/formularios.ts` deduce el tipo de cada campo de las respuestas que hay
 * (número, fecha, sí/no, opción, varias opciones, correo, teléfono, página, texto, texto largo) y con eso decide:
 *  - qué columnas salen de inicio (hasta 5: opciones, sí/no, números y fechas; nunca un texto largo ni un campo que
 *    dice lo mismo en todas), y el botón "Columnas" agrega o quita (se recuerda por formulario en este navegador);
 *  - el filtro de cada campo (casillas, de–a, "contiene") y el orden (tocar el encabezado);
 *  - la gráfica de cada campo en la pestaña "Analítica" (barras con %, histograma con media y mediana, línea del
 *    tiempo, palabras que más se repiten), con los MISMOS filtros que la tabla.
 * Lo largo (comentarios) y todo lo demás se ve al abrir la fila, donde también está "Descartar".
 *
 * Se trae lo del periodo de una vez (hasta 500 registros; al día de hoy hay ~120 en total) y se filtra aquí. Si el
 * periodo trae más de 500, al elegir un formulario se le pide al servidor sólo ése.
 */

interface EstadoHalory { estado: string; motivo: string | null; at: string | null; lead: string | null }
interface Archivo { nombre: string; tipo: string; bytes: number | null; campo: string; estado: string; error: string | null; url: string | null }
interface Registro {
  id: string; fuente: string; formulario: string | null; nombre: string | null; telefono: string | null; email: string | null; redes: string | null;
  campos: Record<string, unknown>; creado: string | null; actualizado: string | null; completo: boolean;
  prueba?: boolean; halory?: EstadoHalory; archivos?: Archivo[];
}
interface Resumen { total: number; completos: number; truncado: boolean }
interface Respuesta { resumen: Resumen; registros: Registro[] }

const FUENTE_LABEL: Record<string, string> = { safevity: 'Safevity · franquicias', sandyduenas: 'Sandy Dueñas', duenadetubelleza: 'Sitio DTB', halory: 'Halory', otro: 'Otro' };
// Subcategoría dentro de una fuente (el sitio la manda en `payload.formulario`). Lo que no esté aquí sale con su nombre.
const FORMULARIO_LABEL: Record<string, string> = {
  'metodo-campo': 'Medicina Estética Cuántica · conferencia', 'lp-botox': 'Landing Botox', 'lp-pdrn': 'Landing PDRN',
  'test-transformacion': 'Test de transformación (anónimo)', 'checkout-nivel-1': 'Checkout nivel 1', 'checkout-nivel-2': 'Checkout nivel 2', 'checkout-nivel-3': 'Checkout nivel 3',
};
// Las páginas de Halory traen su título (`_pagina_titulo`): se aprende de los registros cargados.
const TITULOS_PAGINA = new Map<string, string>();
const formularioLabel = (f: string, fuente?: string) => (fuente === 'halory' && TITULOS_PAGINA.get(f)) || FORMULARIO_LABEL[f] || f;
const claveForma = (r: { fuente: string; formulario: string | null }) => `${r.fuente}|${r.formulario || ''}`;
const nombreForma = (k: string) => {
  const [f, s] = k.split('|'); const fl = FUENTE_LABEL[f] || f;
  return s ? `${fl}${f === 'halory' ? ' · ' : ' › '}${formularioLabel(s, f)}` : fl;
};

// ¿Llegó a Halory? (0.48.0) — la pastilla de la tabla; el porqué completo va en el detalle (en celular no hay `title`).
const HALORY_TXT: Record<string, { t: string; bg: string; c: string }> = {
  enviado: { t: 'en Halory ✓', bg: '#d1fae5', c: '#065f46' },
  de_halory: { t: 'de Halory', bg: 'var(--gold-light)', c: 'var(--gold-dark)' },
  por_mandar: { t: 'por mandar', bg: '#fef3c7', c: '#92400e' },
  en_cola: { t: 'en la cola', bg: '#fef3c7', c: '#92400e' },
  error: { t: 'error', bg: '#fee2e2', c: '#b42318' },
  detenido: { t: 'detenido', bg: '#fef3c7', c: '#92400e' },
  anterior: { t: 'no se mandó', bg: '#f3f4f6', c: '#4b5563' },
  no_se_manda: { t: 'no se manda', bg: '#f3f4f6', c: '#4b5563' },
};
function textoHalory(h?: EstadoHalory): string {
  if (!h) return '';
  if (h.estado === 'enviado') return `En Halory ✓${h.at ? ` desde ${fmt(h.at)}` : ''}`;
  if (h.estado === 'de_halory') return 'Llegó de una página de Halory (no se manda de vuelta)';
  const base = (HALORY_TXT[h.estado]?.t || h.estado);
  return h.motivo ? `${base.charAt(0).toUpperCase()}${base.slice(1)}: ${h.motivo}` : base;
}
function PastillaHalory({ h }: { h?: EstadoHalory }) {
  if (!h) return <span className="muted">—</span>;
  const x = HALORY_TXT[h.estado] || HALORY_TXT.no_se_manda;
  return <span title={textoHalory(h)} data-halory={h.estado} style={{ padding: '2px 8px', borderRadius: 8, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', background: x.bg, color: x.c }}>{x.t}</span>;
}
const pesoTxt = (b: number | null) => (b == null ? '' : b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b >= 1024 ? `${Math.round(b / 1024)} KB` : `${b} B`);
function Archivos({ xs }: { xs: Archivo[] }) {
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }} data-archivos>
      {xs.map((a, i) => {
        const imagen = /^image\//.test(a.tipo) && a.url;
        return (
          <div key={i} style={{ width: 132, minWidth: 0, fontSize: 12 }} data-archivo={a.estado}>
            {a.url ? (
              <a href={a.url} target="_blank" rel="noreferrer" style={{ display: 'block', textDecoration: 'none' }} title={`Abrir ${a.nombre}`}>
                {imagen
                  ? <img src={a.url} alt={a.nombre} loading="lazy" style={{ width: 132, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', display: 'block', background: '#f3ede1' }} />
                  : <div style={{ width: 132, height: 96, borderRadius: 8, border: '1px solid var(--border)', display: 'grid', placeItems: 'center', background: '#fffdf8', color: 'var(--gold-dark)', fontWeight: 600 }}>{(a.nombre.split('.').pop() || 'archivo').toUpperCase().slice(0, 5)}</div>}
              </a>
            ) : (
              <div style={{ width: 132, height: 96, borderRadius: 8, border: '1px dashed var(--border)', display: 'grid', placeItems: 'center', textAlign: 'center', padding: 6, boxSizing: 'border-box', color: a.estado === 'copiando' ? '#92400e' : '#b42318' }}>
                {a.estado === 'copiando' ? 'copiando…' : 'no se pudo copiar'}
              </div>
            )}
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 3 }} title={a.nombre}>{a.nombre}</div>
            <div className="muted" style={{ fontSize: 11 }}>{a.url ? pesoTxt(a.bytes) : a.estado === 'copiando' ? 'se copia solo en unos minutos' : (a.error || '')}</div>
          </div>
        );
      })}
    </div>
  );
}

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const fmtCorta = (iso: string | null) => (iso ? new Date(iso).toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }) : '—');
const soloDigitos = (s: string) => s.replace(/\D/g, '');
const pct = (n: number, d: number) => (d ? Math.round((n / d) * 100) : 0);
const COLS_KEY = (k: string) => `dtb_hub_formularios_cols:${k || '*'}`;
const leerCols = (k: string): string[] | null => { try { const v = JSON.parse(localStorage.getItem(COLS_KEY(k)) || 'null'); return Array.isArray(v) ? v.map(String) : null; } catch { return null; } };
const guardarCols = (k: string, v: string[] | null) => { try { if (v) localStorage.setItem(COLS_KEY(k), JSON.stringify(v)); else localStorage.removeItem(COLS_KEY(k)); } catch { /* sin almacenamiento */ } };

type Orden = { col: string; dir: 1 | -1 };
type Estado = '' | 'completo' | 'incompleto';
const POR_PAGINA = 50;
const ORO = '#AC996C';

const CSS = `
.fw-formas { display: flex; gap: 8px; flex-wrap: wrap; margin: 10px 0 12px; }
.fw-forma { text-align: left; padding: 9px 13px; border-radius: 14px; border: 1px solid var(--border); background: #fff; cursor: pointer; font: inherit; min-width: 0; max-width: 100%; }
.fw-forma[aria-pressed="true"] { border-color: var(--gold); background: var(--gold-light); box-shadow: inset 0 0 0 1px var(--gold); }
.fw-forma .t { font-size: 12.5px; color: var(--ink-soft); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 300px; }
.fw-forma .n { font-size: 19px; font-weight: 300; color: var(--gold-dark); }
.fw-forma .n small { font-size: 12px; color: var(--ink-soft); }
/* En celular los formularios van en UN renglón que se desliza (tres tarjetas apiladas se comían la pantalla). */
@media (max-width: 699px) {
  .fw-formas { flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; margin-right: -16px; padding: 0 16px 2px 0; }
  .fw-formas::-webkit-scrollbar { display: none; }
  .fw-forma { flex: 0 0 auto; max-width: 78vw; }
  .fw-forma .t { max-width: 70vw; }
}
.fw-panel input[type=checkbox], .fw-panel input[type=radio] { accent-color: #8b7355; width: auto; }
.fw-barra { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin: 4px 0 10px; }
.fw-barra input[type=search] { flex: 1 1 220px; min-width: 0; padding: 8px 12px; border: 1px solid var(--border); border-radius: 10px; font: inherit; font-size: 14px; background: #fff; }
.fw-chip { display: inline-flex; align-items: center; gap: 6px; padding: 3px 6px 3px 10px; border-radius: 999px; background: var(--gold-light); color: var(--gold-dark); font-size: 12.5px; border: 1px solid var(--gold); max-width: 100%; }
.fw-chip span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fw-chip button { all: unset; cursor: pointer; width: 18px; height: 18px; border-radius: 50%; text-align: center; line-height: 18px; font-size: 14px; }
.fw-chip button:hover, .fw-chip button:focus-visible { background: rgba(0,0,0,.08); }
.fw-panel { padding: 12px 14px; margin-bottom: 12px; display: grid; gap: 12px; grid-template-columns: repeat(auto-fill, minmax(min(100%, 230px), 1fr)); }
.fw-panel h5 { margin: 0 0 6px; font-size: 12px; color: var(--ink-soft); font-weight: 700; text-transform: uppercase; letter-spacing: .04em; }
.fw-panel label { display: flex; align-items: center; gap: 7px; font-size: 13px; margin: 3px 0; cursor: pointer; }
.fw-panel input[type=number], .fw-panel input[type=date], .fw-panel input[type=text] { width: 100%; min-width: 0; padding: 6px 9px; border: 1px solid var(--border); border-radius: 8px; font: inherit; font-size: 13px; }
.fw-tabla { width: 100%; border-collapse: collapse; }
.fw-tabla th { text-align: left; padding: 0; font-size: 12px; color: var(--ink-soft); font-weight: 500; white-space: nowrap; border-bottom: 1px solid var(--border); }
.fw-tabla th button { all: unset; display: inline-flex; gap: 4px; align-items: center; padding: 9px 10px; cursor: pointer; border-radius: 8px; }
.fw-tabla th button:hover, .fw-tabla th button:focus-visible { background: var(--gold-light); color: var(--gold-dark); }
.fw-tabla td { text-align: left; padding: 9px 10px; font-size: 13px; border-top: 1px solid var(--border); vertical-align: top; }
.fw-tabla tr.fila { cursor: pointer; }
.fw-tabla tr.fila:hover td { background: #fffdf8; }
.fw-tabla tr.fila[aria-expanded="true"] td { background: var(--gold-light); }
.fw-det { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 220px), 1fr)); gap: 8px 16px; }
.fw-det dt { font-size: 11.5px; color: var(--ink-soft); }
.fw-det dd { margin: 0; font-size: 13px; white-space: pre-wrap; overflow-wrap: anywhere; }
.fw-graficas { display: grid; gap: 12px; grid-template-columns: repeat(auto-fill, minmax(min(100%, 340px), 1fr)); }
.fw-barras { display: grid; gap: 6px; }
.fw-barras button { all: unset; display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 2px 8px; cursor: pointer; padding: 3px 4px; border-radius: 8px; font-size: 13px; }
.fw-barras button:hover, .fw-barras button:focus-visible { background: #fffaf0; }
.fw-barras button[aria-pressed="true"] { background: var(--gold-light); }
.fw-barras .et { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fw-barras .val { color: var(--ink-soft); font-variant-numeric: tabular-nums; white-space: nowrap; }
.fw-barras .pista { grid-column: 1 / -1; height: 10px; border-radius: 0 4px 4px 0; background: #f3ede1; overflow: hidden; }
.fw-barras .pista i { display: block; height: 100%; background: ${ORO}; border-radius: 0 4px 4px 0; }
`;

function Estadito({ completo, anonimo }: { completo: boolean; anonimo?: boolean }) {
  // Un formulario anónimo (el test) no pide contacto: no está "incompleto".
  if (anonimo) return <span title="No pide datos de contacto" style={{ padding: '2px 8px', borderRadius: 8, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', background: '#f3f4f6', color: '#4b5563' }}>Anónimo</span>;
  return <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', background: completo ? 'var(--success-bg, #d1fae5)' : 'var(--warning-bg, #fef3c7)', color: completo ? '#065f46' : '#92400e' }}>{completo ? 'Completo' : 'Incompleto'}</span>;
}
const aprenderTitulos = (x: Respuesta) => {
  for (const r of x.registros || []) if (r.fuente === 'halory' && r.formulario && typeof r.campos?._pagina_titulo === 'string') TITULOS_PAGINA.set(r.formulario, String(r.campos._pagina_titulo));
  return x;
};

export function FormulariosWeb() {
  const [todos, setTodos] = useState<Respuesta | null>(null);
  const [soloForma, setSoloForma] = useState<Respuesta | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Periodo con el selector de todas las pantallas; arranca con el de la clínica (Administración → Preferencias).
  const [per, setPer] = useState(usePeriodoInicial());
  const [forma, setForma] = useState('');
  const [fuenteSel, setFuenteSel] = useState('');
  const [vista, setVista] = useState<'respuestas' | 'analitica'>('respuestas');
  const [q, setQ] = useState('');
  const [estado, setEstado] = useState<Estado>('');
  const [filtros, setFiltros] = useState<Filtros>({});
  const [orden, setOrden] = useState<Orden>({ col: '__fecha', dir: -1 });
  const [elegidas, setElegidas] = useState<string[] | null>(() => leerCols(''));
  const [panel, setPanel] = useState<'' | 'filtros' | 'columnas' | 'halory'>('');
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const [mostrar, setMostrar] = useState(POR_PAGINA);
  // El detalle de una fila se pinta del ancho que SE VE de la tabla (pegado a la izquierda), no del ancho de la tabla
  // completa: en celular la tabla mide ~1,000 px y el texto del detalle se cortaba a la derecha del scroll.
  const [anchoCaja, setAnchoCaja] = useState(0);
  const obs = useRef<ResizeObserver | null>(null);
  const refCaja = useCallback((el: HTMLDivElement | null) => {
    obs.current?.disconnect(); obs.current = null;
    if (!el) return;
    setAnchoCaja(el.clientWidth);
    if (typeof ResizeObserver !== 'undefined') { obs.current = new ResizeObserver(() => setAnchoCaja(el.clientWidth)); obs.current.observe(el); }
  }, []);
  const [ocupado, setOcupado] = useState<string | null>(null);

  const load = () => {
    setError(null);
    const qs = ponerPeriodo(new URLSearchParams(), per, 'todo');
    return api<Respuesta>(`formularios-web?${qs.toString()}`).then((x) => setTodos(aprenderTitulos(x))).catch((e) => setError(textoDeError(e)));
  };
  useEffect(() => { setTodos(null); load(); }, [per]);
  // Con más de 500 registros en el periodo, el formulario elegido se pide aparte (completo, hasta 500 suyos).
  useEffect(() => {
    setSoloForma(null);
    if (!forma || !todos?.resumen.truncado) return;
    const [fuente, formulario] = forma.split('|');
    const qs = ponerPeriodo(new URLSearchParams({ fuente, ...(formulario ? { formulario } : {}) }), per, 'todo');
    api<Respuesta>(`formularios-web?${qs.toString()}`).then((x) => setSoloForma(aprenderTitulos(x))).catch((e) => setError(textoDeError(e)));
  }, [forma, todos]);
  // Cambiar de formulario: sus columnas recordadas, sin los filtros del otro.
  const elegirForma = (k: string) => { setForma(k); setFiltros({}); setElegidas(leerCols(k)); setAbiertos(new Set()); setMostrar(POR_PAGINA); setOrden({ col: '__fecha', dir: -1 }); };

  // Los formularios del periodo (tarjetas de arriba): uno por fuente y subcategoría.
  const formas = useMemo(() => {
    const m = new Map<string, { k: string; total: number; completos: number; ultimo: string | null }>();
    for (const r of todos?.registros || []) {
      const k = claveForma(r); const a = m.get(k) || { k, total: 0, completos: 0, ultimo: null };
      a.total++; if (r.completo) a.completos++; if (!a.ultimo || (r.creado && r.creado > a.ultimo)) a.ultimo = r.creado;
      m.set(k, a);
    }
    return [...m.values()].sort((a, b) => b.total - a.total);
  }, [todos]);
  // Las fuentes del periodo (Sitio DTB, Halory, Safevity…): el filtro de arriba (0.48.0).
  const fuentes = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of todos?.registros || []) m.set(r.fuente, (m.get(r.fuente) || 0) + 1);
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [todos]);
  const formasVisibles = formas.filter((f) => !fuenteSel || f.k.startsWith(`${fuenteSel}|`));
  const elegirFuente = (f: string) => { setFuenteSel(f); if (forma && f && !forma.startsWith(`${f}|`)) elegirForma(''); setMostrar(POR_PAGINA); };

  const base = useMemo(() => {
    const fuente = soloForma || todos;
    return (fuente?.registros || []).filter((r) => (!fuenteSel || r.fuente === fuenteSel) && (!forma || claveForma(r) === forma));
  }, [todos, soloForma, forma, fuenteSel]);
  const campos = useMemo(() => inferirCampos(base), [base]);
  const porClave = useMemo(() => new Map(campos.map((c) => [c.clave, c])), [campos]);
  const deInicio = useMemo(() => columnasDeInicio(campos, 5), [campos]);
  const columnas = useMemo(() => (elegidas ? elegidas.filter((k) => porClave.has(k)) : deInicio).map((k) => porClave.get(k)!), [elegidas, deInicio, porClave]);
  const setColumnas = (ks: string[] | null) => { setElegidas(ks); guardarCols(forma, ks); };

  const filtrosActivos = Object.entries(filtros).filter(([k, f]) => porClave.has(k) && filtroActivo(f));
  const filas = useMemo(() => {
    const t = q.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const xs = base.filter((r) => {
      if (estado === 'completo' && !r.completo) return false;
      if (estado === 'incompleto' && r.completo) return false;
      for (const [k, f] of filtrosActivos) if (!cumple(porClave.get(k)!, r.campos[k], f)) return false;
      if (t) {
        const todo = [r.nombre, r.telefono, r.email, r.redes, ...Object.entries(r.campos).map(([k, v]) => { const c = porClave.get(k); return c ? textoValor(k, c.tipo, v) : ''; })]
          .join(' ').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
        if (!todo.includes(t) && !(soloDigitos(t).length >= 4 && soloDigitos(`${r.telefono || ''}`).includes(soloDigitos(t)))) return false;
      }
      return true;
    });
    const c = porClave.get(orden.col);
    return xs.sort((a, b) => {
      if (orden.col === '__fecha') return ((a.creado || '') < (b.creado || '') ? -1 : (a.creado || '') > (b.creado || '') ? 1 : 0) * orden.dir;
      if (orden.col === '__nombre') return (a.nombre ? (b.nombre ? a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }) * orden.dir : -1) : b.nombre ? 1 : 0);
      if (orden.col === '__estado') return (Number(a.completo) - Number(b.completo)) * orden.dir;
      return c ? comparar(c, a.campos[c.clave], b.campos[c.clave], orden.dir) : 0;
    });
  }, [base, estado, JSON.stringify(filtrosActivos), q, orden, porClave]);

  const ponerFiltro = (k: string, f: Filtro | null) => { setMostrar(POR_PAGINA); setFiltros((x) => { const n = { ...x }; if (f && filtroActivo(f)) n[k] = f; else delete n[k]; return n; }); };
  const alternarValor = (c: Campo, v: string) => {
    const f = filtros[c.clave]; const vs = f?.tipo === 'valores' ? f.valores : [];
    ponerFiltro(c.clave, { tipo: 'valores', valores: vs.includes(v) ? vs.filter((x) => x !== v) : [...vs, v] });
  };
  const quitarTodo = () => { setFiltros({}); setEstado(''); setQ(''); };
  const nFiltros = filtrosActivos.length + (estado ? 1 : 0);
  const ordenar = (col: string) => setOrden((o) => (o.col === col ? { col, dir: (o.dir === 1 ? -1 : 1) as 1 | -1 } : { col, dir: col === '__fecha' ? -1 : 1 }));

  async function descartar(r: Registro) {
    const quien = r.nombre || r.telefono || 'Este registro';
    if (!(await confirmar({ titulo: '¿Descartar este registro?', peligro: true, confirmar: 'Descartar', mensaje: `${quien} deja de verse aquí. No se borra de la base.` }))) return;
    setOcupado(r.id);
    try { await api(`formularios-web/${r.id}`, { method: 'DELETE' }); await load(); }
    catch (e) { setError(textoDeError(e, 'No se pudo descartar.')); }
    finally { setOcupado(null); }
  }

  function bajarExcel() {
    const cols: ColExcel[] = [{ titulo: 'Fecha', tipo: 'fechahora' }, { titulo: 'Formulario' }, { titulo: 'Nombre' }, { titulo: 'Teléfono' }, { titulo: 'Correo' }, { titulo: 'Redes' }, { titulo: 'Estado' }, { titulo: 'Halory' }, { titulo: 'Archivos', tipo: 'numero' },
      ...campos.map((c) => ({ titulo: c.etiqueta, tipo: c.tipo === 'numero' ? 'numero' as const : c.tipo === 'fecha' ? 'fecha' as const : 'texto' as const }))];
    const rows: Celda[][] = filas.map((r) => [r.creado, nombreForma(claveForma(r)), r.nombre, r.telefono, r.email, r.redes, r.campos.anonimo === true ? 'Anónimo' : r.completo ? 'Completo' : 'Incompleto',
      textoHalory(r.halory), (r.archivos || []).length,
      ...campos.map((c) => { const v = r.campos[c.clave]; return c.tipo === 'numero' ? numero(v) : c.tipo === 'fecha' ? fechaDia(v) : textoValor(c.clave, c.tipo, v); })]);
    descargarExcel(nombreArchivo('formularios', forma ? nombreForma(forma) : 'todos', periodoTexto(per)), [{ nombre: 'Respuestas', columnas: cols, filas: rows }]);
  }

  const mezclados = !forma && formas.length > 1;
  const Encabezado = ({ col, children }: { col: string; children: React.ReactNode }) => (
    <th aria-sort={orden.col === col ? (orden.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => ordenar(col)} data-ordenar={col} title="Ordenar">{children}<span aria-hidden="true" style={{ fontSize: 10, opacity: orden.col === col ? 1 : 0.3 }}>{orden.col === col && orden.dir === 1 ? '▲' : '▼'}</span></button>
    </th>
  );

  return (
    <div>
      <TopBar />
      <style>{CSS}</style>
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '18px 16px 48px' }}>
        <h1 style={{ margin: '4px 2px 4px', fontSize: 24, fontWeight: 300, color: 'var(--gold-dark)' }}>Formularios web</h1>
        <p className="muted" style={{ marginTop: 0, fontSize: 13.5 }}>
          Lo que la gente llena en los sitios (las landing pages y el sitio de DTB, franquicias de <strong>safevity.com</strong>, <strong>sandyduenas.com</strong>) y en
          las páginas de <strong>Halory</strong>, aparte del registro de pacientes. <strong>Incompleto</strong> = empezó el formulario y no dejó nombre ni teléfono.
          Los de pacientes se mandan a Halory para que alguien les marque (botón <strong>Halory</strong>).
        </p>

        <div style={{ margin: '4px 0 6px' }}><PeriodPicker value={per} onChange={(v) => { setPer(v); setMostrar(POR_PAGINA); }} /></div>
        {error && <div className="card" style={{ padding: 14, color: 'var(--warning)', background: 'var(--warning-bg)', marginTop: 10 }} role="alert">{error}</div>}
        {!todos && !error && <div className="muted" style={{ padding: 40, textAlign: 'center' }}>Cargando…</div>}

        {todos && formas.length === 0 && (
          <div className="card" style={{ padding: '12px 14px', fontSize: 13, marginTop: 10 }} data-vacio>
            Sin registros ({periodoTexto(per).toLowerCase()}). Si un formulario se llenó y aquí no aparece, ese sitio no está guardando: abre su <code>/api/ping</code> (safevity.com o sandyduenas.com) — dice si tiene la llave de DraIA.
          </div>
        )}

        {todos && formas.length > 0 && (
          <>
            {fuentes.length > 1 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '10px 0 0' }} role="group" aria-label="Fuente" data-fuentes>
                {[['', 'Todas las fuentes', todos.registros.length] as [string, string, number], ...fuentes.map(([f, nn]) => [f, FUENTE_LABEL[f] || f, nn] as [string, string, number])].map(([f, l, nn]) => (
                  <button key={f || 'todas'} type="button" className="fw-chip" aria-pressed={fuenteSel === f} data-fuente={f || 'todas'} onClick={() => elegirFuente(f)}
                    style={{ cursor: 'pointer', padding: '4px 11px', background: fuenteSel === f ? 'var(--gold-light)' : '#fff', borderColor: fuenteSel === f ? 'var(--gold)' : 'var(--border)', color: fuenteSel === f ? 'var(--gold-dark)' : 'var(--ink-soft)', font: 'inherit', fontSize: 12.5 }}>
                    {l} · {nn}
                  </button>
                ))}
              </div>
            )}
            {/* Cada tarjeta ES el filtro de formulario (antes: tarjetas de resumen + dos renglones de pastillas). */}
            <div className="fw-formas" role="group" aria-label="Formulario">
              <button type="button" className="fw-forma" aria-pressed={!forma} onClick={() => elegirForma('')} data-forma="todos">
                <div className="t">Todos los formularios</div>
                <div className="n">{todos.resumen.completos} <small>completos · {todos.resumen.total - todos.resumen.completos} incompletos</small></div>
              </button>
              {formasVisibles.map((f) => (
                <button key={f.k} type="button" className="fw-forma" aria-pressed={forma === f.k} onClick={() => elegirForma(f.k)} data-forma={f.k} title={`${nombreForma(f.k)} · último: ${fmt(f.ultimo)}`}>
                  <div className="t">{nombreForma(f.k)}</div>
                  <div className="n">{f.completos} <small>completos · {f.total - f.completos} incompletos · último {fmt(f.ultimo)}</small></div>
                </button>
              ))}
            </div>
            {todos.resumen.truncado && <p className="muted" style={{ fontSize: 12, margin: '-4px 0 10px' }}>El periodo trae más de 500 registros: los totales de arriba son de los 500 más recientes. Al elegir un formulario se traen sólo los suyos.</p>}

            <Pestanas items={[['respuestas', 'Respuestas'], ['analitica', 'Analítica']] as const} valor={vista} onCambio={setVista} etiqueta="Vista de los formularios" />

            <div className="fw-barra">
              <input type="search" value={q} onChange={(e) => { setQ(e.target.value); setMostrar(POR_PAGINA); }} placeholder="Buscar nombre, contacto o respuesta" aria-label="Buscar en las respuestas" data-buscar />
              <button type="button" className="btn-ghost" aria-expanded={panel === 'filtros'} onClick={() => setPanel(panel === 'filtros' ? '' : 'filtros')} data-abrir="filtros" style={{ padding: '7px 14px', fontSize: 13.5, boxShadow: 'none' }}>
                Filtros{nFiltros ? ` (${nFiltros})` : ''}
              </button>
              {vista === 'respuestas' && (
                <button type="button" className="btn-ghost" aria-expanded={panel === 'columnas'} onClick={() => setPanel(panel === 'columnas' ? '' : 'columnas')} data-abrir="columnas" style={{ padding: '7px 14px', fontSize: 13.5, boxShadow: 'none' }}>
                  Columnas ({columnas.length})
                </button>
              )}
              <button type="button" className="btn-ghost" aria-expanded={panel === 'halory'} onClick={() => setPanel(panel === 'halory' ? '' : 'halory')} data-abrir="halory" style={{ padding: '7px 14px', fontSize: 13.5, boxShadow: 'none' }}>
                Halory
              </button>
              <MasMenu datos="formularios" ariaLabel="Más acciones de formularios" items={[
                { etiqueta: `Bajar a Excel (${filas.length})`, onClick: bajarExcel, disabled: !filas.length, accion: 'excel' },
                nFiltros || q ? { etiqueta: 'Quitar filtros y búsqueda', onClick: quitarTodo, accion: 'quitar-filtros' } : null,
                elegidas ? { etiqueta: 'Volver a las columnas de inicio', onClick: () => setColumnas(null), accion: 'columnas-inicio' } : null,
              ]} />
            </div>

            {(nFiltros > 0) && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }} data-filtros-activos>
                {estado && <span className="fw-chip"><span>{estado === 'completo' ? 'Sólo completos' : 'Sólo incompletos'}</span><button type="button" aria-label="Quitar filtro de estado" onClick={() => setEstado('')}>×</button></span>}
                {filtrosActivos.map(([k, f]) => (
                  <span key={k} className="fw-chip" data-chip={k}><span>{filtroTexto(porClave.get(k)!, f)}</span><button type="button" aria-label={`Quitar filtro de ${porClave.get(k)!.etiqueta}`} onClick={() => ponerFiltro(k, null)}>×</button></span>
                ))}
              </div>
            )}

            {panel === 'halory' && <PanelHalory onCerrar={() => setPanel('')} onCambio={() => { load(); }} />}
            {panel === 'filtros' && <PanelFiltros campos={campos} filtros={filtros} estado={estado} setEstado={setEstado} ponerFiltro={ponerFiltro} alternarValor={alternarValor} onCerrar={() => setPanel('')} />}
            {panel === 'columnas' && vista === 'respuestas' && (
              <PanelColumnas campos={campos} visibles={columnas.map((c) => c.clave)} esInicio={!elegidas}
                onCambio={(ks) => setColumnas(ks)} onInicio={() => setColumnas(null)} onCerrar={() => setPanel('')} />
            )}
            {mezclados && <p className="muted" style={{ fontSize: 12.5, margin: '0 0 10px' }} data-mezclados>Hay respuestas de {formas.length} formularios distintos: elige uno arriba para ver sus columnas y gráficas sin mezclar.</p>}

            {vista === 'respuestas' ? (
              filas.length === 0 ? (
                <div className="card muted" style={{ padding: 24, textAlign: 'center', fontSize: 13.5 }} data-sin-filas>
                  {base.length ? <>Ninguna respuesta cumple con los filtros ({base.length} en total). <button type="button" className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12.5 }} onClick={quitarTodo}>Quitar filtros</button></> : 'Sin respuestas en este periodo.'}
                </div>
              ) : (
                <div className="card" ref={refCaja} style={{ padding: 4, overflowX: 'auto' }} data-tabla="formularios">
                  <table className="fw-tabla" style={{ minWidth: 440 + columnas.length * 105 }}>
                    <thead>
                      <tr>
                        <Encabezado col="__fecha">Fecha</Encabezado>
                        <Encabezado col="__nombre">Nombre</Encabezado>
                        <th><span style={{ display: 'inline-block', padding: '9px 10px' }}>Contacto</span></th>
                        {columnas.map((c) => <Encabezado key={c.clave} col={c.clave}><span data-col={c.clave}>{c.etiqueta}</span></Encabezado>)}
                        <th><span style={{ display: 'inline-block', padding: '9px 10px' }}>Halory</span></th>
                        <Encabezado col="__estado">Estado</Encabezado>
                      </tr>
                    </thead>
                    <tbody>
                      {filas.slice(0, mostrar).map((r) => {
                        const abierto = abiertos.has(r.id);
                        const alternar = () => setAbiertos((s) => { const n = new Set(s); if (n.has(r.id)) n.delete(r.id); else n.add(r.id); return n; });
                        return [
                          <tr key={r.id} className="fila" aria-expanded={abierto} tabIndex={0} data-fila={r.id} style={{ opacity: r.completo ? 1 : 0.78 }}
                            onClick={(e) => { if ((e.target as HTMLElement).closest('a')) return; alternar(); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); alternar(); } }}>
                            <td style={{ whiteSpace: 'nowrap' }}><span aria-hidden="true" style={{ display: 'inline-block', width: 12, color: 'var(--ink-soft)', fontSize: 10 }}>{abierto ? '▾' : '▸'}</span>{fmtCorta(r.creado)}</td>
                            <td style={{ fontWeight: 500, minWidth: 120 }}>
                              {r.nombre || <span className="muted">—</span>}
                              {!forma && <div style={{ fontSize: 11, color: 'var(--gold-dark)', fontWeight: 600 }}>{nombreForma(claveForma(r))}</div>}
                            </td>
                            <td style={{ minWidth: 170 }}>
                              {r.telefono && <div style={{ whiteSpace: 'nowrap' }}><a href={`https://wa.me/${soloDigitos(r.telefono)}`} target="_blank" rel="noreferrer">{r.telefono}</a></div>}
                              {r.email && <div style={{ overflowWrap: 'break-word', wordBreak: 'normal', fontSize: 12.5 }}><a href={`mailto:${r.email}`}>{r.email}</a></div>}
                              {!r.telefono && !r.email && <span className="muted">{r.redes || '—'}</span>}
                            </td>
                            {columnas.map((c) => {
                              const t = textoValor(c.clave, c.tipo, r.campos[c.clave]);
                              return <td key={c.clave} data-celda={c.clave} style={{ maxWidth: 240, whiteSpace: c.tipo === 'numero' ? 'nowrap' : undefined, textAlign: c.tipo === 'numero' ? 'right' : undefined }}>
                                {t ? <span title={t.length > 80 ? t : undefined}>{t.length > 80 ? t.slice(0, 78) + '…' : t}</span> : <span className="muted">—</span>}
                              </td>;
                            })}
                            <td><PastillaHalory h={r.halory} />{(r.archivos || []).length > 0 && <div className="muted" style={{ fontSize: 11, marginTop: 3, whiteSpace: 'nowrap' }}>{(r.archivos || []).length} archivo{(r.archivos || []).length === 1 ? '' : 's'}</div>}</td>
                            <td><Estadito completo={r.completo} anonimo={r.campos.anonimo === true} />{r.prueba && <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>de prueba</div>}</td>
                          </tr>,
                          abierto && (
                            <tr key={r.id + ':d'} data-detalle={r.id}>
                              <td colSpan={5 + columnas.length} style={{ background: '#fffdf8', padding: 0 }}>
                                <div style={{ position: 'sticky', left: 0, boxSizing: 'border-box', width: anchoCaja > 40 ? anchoCaja - 8 : undefined, padding: '12px 14px' }} data-detalle-caja>
                                <dl className="fw-det" style={{ margin: 0 }}>
                                  <div><dt>Formulario</dt><dd>{nombreForma(claveForma(r))}</dd></div>
                                  <div><dt>Llegó</dt><dd>{fmt(r.creado)}{r.actualizado && r.actualizado !== r.creado ? ` · actualizado ${fmt(r.actualizado)}` : ''}</dd></div>
                                  {r.redes && <div><dt>Redes</dt><dd>{r.redes}</dd></div>}
                                  <div style={{ gridColumn: '1 / -1' }} data-halory-detalle><dt>Halory</dt><dd>{textoHalory(r.halory) || '—'}</dd></div>
                                  {typeof r.campos._pagina_titulo === 'string' && (
                                    <div><dt>Página de Halory</dt><dd>{typeof r.campos._pagina_url === 'string' ? <a href={String(r.campos._pagina_url)} target="_blank" rel="noreferrer">{String(r.campos._pagina_titulo)}</a> : String(r.campos._pagina_titulo)}{typeof r.campos._form === 'string' ? ` · ${r.campos._form}` : ''}</dd></div>
                                  )}
                                  {r.prueba && <div><dt>Prueba</dt><dd>Cuenta de prueba en Halory</dd></div>}
                                  {campos.map((c) => {
                                    const t = textoValor(c.clave, c.tipo, r.campos[c.clave]);
                                    return t ? <div key={c.clave} style={c.tipo === 'texto_largo' ? { gridColumn: '1 / -1' } : undefined}><dt>{c.etiqueta}</dt><dd>{t}</dd></div> : null;
                                  })}
                                </dl>
                                {(r.archivos || []).length > 0 && (
                                  <div style={{ marginTop: 12 }}><div className="muted" style={{ fontSize: 11.5, marginBottom: 6 }}>Archivos ({(r.archivos || []).length}) · copiados al almacén de DTB</div><Archivos xs={r.archivos || []} /></div>
                                )}
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                                  {r.telefono && <a className="btn-ghost" style={{ padding: '5px 12px', fontSize: 12.5, textDecoration: 'none' }} href={`https://wa.me/${soloDigitos(r.telefono)}`} target="_blank" rel="noreferrer">WhatsApp</a>}
                                  {r.email && <a className="btn-ghost" style={{ padding: '5px 12px', fontSize: 12.5, textDecoration: 'none' }} href={`mailto:${r.email}`}>Correo</a>}
                                  <button type="button" className="btn-ghost" disabled={ocupado === r.id} onClick={() => descartar(r)} data-descartar={r.id}
                                    title="Borrado suave: deja de verse aquí, sigue en la base" style={{ padding: '5px 12px', fontSize: 12.5, color: 'var(--danger, #b42318)', marginLeft: 'auto' }}>
                                    {ocupado === r.id ? 'Descartando…' : 'Descartar…'}
                                  </button>
                                </div>
                                </div>
                              </td>
                            </tr>
                          ),
                        ];
                      })}
                    </tbody>
                  </table>
                  <div className="muted" style={{ padding: '8px 10px', fontSize: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span data-cuenta>{Math.min(mostrar, filas.length)} de {filas.length}{filas.length !== base.length ? ` (de ${base.length} sin filtros)` : ''} · toca una fila para ver todo lo que contestó</span>
                    {filas.length > mostrar && <button type="button" className="btn-ghost" style={{ padding: '4px 12px', fontSize: 12.5 }} onClick={() => setMostrar(mostrar + POR_PAGINA)} data-ver-mas>Ver {Math.min(POR_PAGINA, filas.length - mostrar)} más</button>}
                  </div>
                  {(soloForma || todos).resumen.truncado && <div className="muted" style={{ padding: '0 10px 10px', fontSize: 12 }}>Se muestran los 500 más recientes — acota las fechas para ver el resto.</div>}
                </div>
              )
            ) : (
              <Analitica campos={campos} filas={filas} base={base.length} filtros={filtros} alternarValor={alternarValor} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ paneles
interface FormaRegla { k: string; fuente: string; formulario: string | null; titulo: string | null; total: number; ultimo: string | null; manda: boolean; porDefault: boolean; motivoDefault: string | null; propia: boolean; desde: string | null; fija: boolean }
interface VistaRegla {
  regla: { activo: boolean; desde: string | null; formularios: Record<string, { manda: boolean; desde: string | null }> };
  actualizada: string | null; por: string | null; llave: boolean; sinLlave: string | null; formas: FormaRegla[]; anteriores: number;
  envios: { enviados: number; errores: number; pendientes: number; detenidos: number; ultimo: string | null; ultimoError: string | null };
}
/** Qué formularios se mandan a Halory (0.48.0): la regla, los números y "Mandar también los anteriores". */
function PanelHalory({ onCerrar, onCambio }: { onCerrar: () => void; onCambio: () => void }) {
  // Qué se manda a Halory (datos de pacientes a otra empresa) lo decide sólo una cuenta de admin (el API contesta 403).
  const { esAdmin } = usePermisos();
  const [v, setV] = useState<VistaRegla | null>(null);
  const [activo, setActivo] = useState(true);
  const [marcas, setMarcas] = useState<Record<string, boolean>>({});
  const [msg, setMsg] = useState<{ t: string; ok: boolean } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const tomar = (x: VistaRegla) => { setV(x); setActivo(x.regla.activo); setMarcas(Object.fromEntries(x.formas.map((f) => [f.k, f.manda]))); };
  useEffect(() => { api<VistaRegla>('formularios-web/regla').then(tomar).catch((e) => setMsg({ t: textoDeError(e), ok: false })); }, []);
  if (!v) return <div className="card fw-panel" data-panel="halory"><div className="muted" style={{ gridColumn: '1 / -1' }}>{msg ? msg.t : 'Cargando…'}</div></div>;
  const cambios = activo !== v.regla.activo || v.formas.some((f) => !f.fija && marcas[f.k] !== f.manda);
  async function guardar() {
    setOcupado(true); setMsg(null);
    try {
      // Sólo viaja lo que se aparta del default (lo demás sigue la regla de siempre).
      const formularios: Record<string, boolean> = {};
      for (const f of v!.formas) if (!f.fija && (marcas[f.k] !== f.porDefault || f.propia)) formularios[f.k] = !!marcas[f.k];
      tomar(await api<VistaRegla>('formularios-web/regla', { method: 'PUT', body: JSON.stringify({ activo, formularios }) }));
      setMsg({ t: 'Regla guardada. Lo nuevo que prendiste se manda desde ahora; lo de antes, sólo con «Mandar también los anteriores».', ok: true });
      onCambio();
    } catch (e) { setMsg({ t: textoDeError(e, 'No se pudo guardar.'), ok: false }); }
    finally { setOcupado(false); }
  }
  async function anteriores() {
    if (!(await confirmar({ titulo: '¿Mandar también los anteriores?', confirmar: `Mandar ${v!.anteriores}`,
      mensaje: `${v!.anteriores} registro${v!.anteriores === 1 ? '' : 's'} que la regla manda pero llegaron antes de prenderla (o se quedaron sin mandar) se crearán en Halory, con sus respuestas. Salen en tandas en los próximos minutos.` }))) return;
    setOcupado(true); setMsg(null);
    try {
      const r = await api<VistaRegla & { encolados: number; enviadosYa: number; motivo: string | null }>('formularios-web/anteriores', { method: 'POST' });
      tomar(r); onCambio();
      setMsg({ t: `${r.encolados} en la cola${r.enviadosYa ? ` · ${r.enviadosYa} ya están en Halory` : ''}. Los demás salen solos en los próximos minutos.${r.motivo ? ` (${r.motivo})` : ''}`, ok: true });
    } catch (e) { setMsg({ t: textoDeError(e, 'No se pudieron poner en la cola.'), ok: false }); }
    finally { setOcupado(false); }
  }
  const e = v.envios;
  return (
    <div className="card fw-panel" data-panel="halory" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }} onKeyDown={(ev) => { if (ev.key === 'Escape' && !document.querySelector('[data-dialogo]')) onCerrar(); }}>
      <div style={{ fontSize: 13 }}>
        <strong>Mandar a Halory</strong> — los formularios de pacientes nacen en Halory, con sus respuestas en las notas, para que alguien les marque.
        Lo que llega de las páginas de Halory ya está allá y no se manda de vuelta.
      </div>
      {!v.llave && <div role="alert" style={{ padding: '8px 12px', borderRadius: 10, background: 'var(--warning-bg, #fef3c7)', color: '#92400e', fontSize: 13 }}>{v.sinLlave}</div>}
      <label style={{ fontSize: 13.5 }} data-regla-activo>
        <input type="checkbox" checked={activo} disabled={!esAdmin} onChange={(x) => setActivo(x.target.checked)} /> Mandar a Halory los formularios nuevos
        {v.regla.activo && v.regla.desde && <span className="muted" style={{ fontSize: 12 }}>· prendido desde {fmt(v.regla.desde)}</span>}
      </label>
      <div>
        <h5>Qué formularios</h5>
        {v.formas.length === 0 && <div className="muted" style={{ fontSize: 13 }}>Todavía no llega ningún formulario.</div>}
        {v.formas.map((f) => (
          <label key={f.k} style={{ alignItems: 'flex-start', opacity: activo ? 1 : 0.6 }} data-regla-forma={f.k}>
            <input type="checkbox" checked={!!marcas[f.k]} disabled={f.fija || !activo || !esAdmin} onChange={(x) => setMarcas((m) => ({ ...m, [f.k]: x.target.checked }))} style={{ marginTop: 3 }} />
            <span style={{ minWidth: 0 }}>
              {f.fuente === 'halory' && f.titulo ? `Halory · ${f.titulo}` : nombreForma(f.k)} <span className="muted">({f.total})</span>
              <span className="muted" style={{ display: 'block', fontSize: 11.5 }}>
                {f.fija ? 'llega de Halory: no se manda de vuelta'
                  : marcas[f.k] ? (f.desde ? `se manda desde ${fmt(f.desde)}` : f.porDefault ? 'se manda (de pacientes)' : 'se manda desde que guardes')
                  : f.porDefault ? 'apagado a mano' : `no se manda: ${f.motivoDefault}`}
              </span>
            </span>
          </label>
        ))}
      </div>
      <div className="muted" style={{ fontSize: 12.5 }} data-regla-numeros>
        En Halory: {e.enviados}{e.pendientes ? ` · en la cola: ${e.pendientes}` : ''}{e.errores ? ` · con error: ${e.errores}` : ''}{e.detenidos ? ` · detenidos: ${e.detenidos}` : ''}
        {e.ultimo ? ` · el último salió ${fmt(e.ultimo)}` : ''}{e.ultimoError ? ` · último error: ${e.ultimoError}` : ''}
      </div>
      {msg && <div role={msg.ok ? 'status' : 'alert'} style={{ padding: '8px 12px', borderRadius: 10, fontSize: 13, background: msg.ok ? 'var(--success-bg, #d1fae5)' : 'var(--warning-bg, #fef3c7)', color: msg.ok ? '#065f46' : '#92400e' }}>{msg.t}</div>}
      {!esAdmin && <div className="muted" style={{ fontSize: 12.5 }} data-solo-admin="formularios-halory">Qué formularios se mandan a Halory lo cambia sólo una cuenta de admin.</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {esAdmin && v.anteriores > 0 && v.regla.activo && v.llave && (
          <button type="button" className="btn-ghost" disabled={ocupado || cambios} onClick={anteriores} data-anteriores title={cambios ? 'Guarda primero los cambios' : undefined} style={{ padding: '6px 14px', fontSize: 13 }}>
            Mandar también los anteriores ({v.anteriores})
          </button>
        )}
        <button type="button" className="btn-ghost" style={{ padding: '6px 14px', fontSize: 13 }} onClick={onCerrar}>Listo</button>
        {esAdmin && <button type="button" className="btn-primary" disabled={!cambios || ocupado} onClick={guardar} data-guardar-regla style={{ padding: '6px 16px', fontSize: 13 }}>{ocupado ? 'Guardando…' : 'Guardar'}</button>}
      </div>
    </div>
  );
}

function PanelFiltros({ campos, filtros, estado, setEstado, ponerFiltro, alternarValor, onCerrar }: {
  campos: Campo[]; filtros: Filtros; estado: Estado; setEstado: (e: Estado) => void; ponerFiltro: (k: string, f: Filtro | null) => void;
  alternarValor: (c: Campo, v: string) => void; onCerrar: () => void;
}) {
  return (
    <div className="card fw-panel" data-panel="filtros" onKeyDown={(e) => { if (e.key === 'Escape' && !document.querySelector('[data-dialogo]')) onCerrar(); }}>
      <div>
        <h5>Estado</h5>
        {([['', 'Todos'], ['completo', 'Sólo completos'], ['incompleto', 'Sólo incompletos']] as const).map(([v, l]) => (
          <label key={v}><input type="radio" name="fw-estado" checked={estado === v} onChange={() => setEstado(v)} /> {l}</label>
        ))}
      </div>
      {campos.filter((c) => !c.constante).map((c) => {
        const f = filtros[c.clave]; const tf = filtroPara(c.tipo);
        return (
          <div key={c.clave} data-filtro={c.clave}>
            <h5 title={`${TIPO_TXT[c.tipo]} · contestaron ${c.respondidas} de ${c.total}`}>{c.etiqueta}</h5>
            {tf === 'valores' && (
              <>
                {c.valores.slice(0, 15).map((v) => (
                  <label key={v.valor}><input type="checkbox" checked={f?.tipo === 'valores' && f.valores.includes(v.valor)} onChange={() => alternarValor(c, v.valor)} />
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.valor}</span><span className="muted" style={{ marginLeft: 'auto', fontSize: 12 }}>{v.n}</span></label>
                ))}
                {c.valores.length > 15 && <div className="muted" style={{ fontSize: 12 }}>+{c.distintos - 15} más (usa la búsqueda)</div>}
              </>
            )}
            {tf === 'rango' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <input type={c.tipo === 'fecha' ? 'date' : 'number'} aria-label={`${c.etiqueta} desde`} placeholder={c.tipo === 'numero' ? `desde ${c.min?.toLocaleString('es-MX') ?? ''}` : undefined}
                  value={f?.tipo === 'rango' ? f.desde : ''} onChange={(e) => ponerFiltro(c.clave, { tipo: 'rango', desde: e.target.value, hasta: f?.tipo === 'rango' ? f.hasta : '' })} />
                <input type={c.tipo === 'fecha' ? 'date' : 'number'} aria-label={`${c.etiqueta} hasta`} placeholder={c.tipo === 'numero' ? `hasta ${c.max?.toLocaleString('es-MX') ?? ''}` : undefined}
                  value={f?.tipo === 'rango' ? f.hasta : ''} onChange={(e) => ponerFiltro(c.clave, { tipo: 'rango', desde: f?.tipo === 'rango' ? f.desde : '', hasta: e.target.value })} />
              </div>
            )}
            {tf === 'contiene' && (
              <input type="text" aria-label={`${c.etiqueta} contiene`} placeholder="contiene…" value={f?.tipo === 'contiene' ? f.texto : ''}
                onChange={(e) => ponerFiltro(c.clave, { tipo: 'contiene', texto: e.target.value })} />
            )}
            <label className="muted" style={{ fontSize: 12 }}>
              <input type="checkbox" checked={f?.tipo === 'respondio' && !f.si} onChange={(e) => ponerFiltro(c.clave, e.target.checked ? { tipo: 'respondio', si: false } : null)} /> sin contestar
            </label>
          </div>
        );
      })}
      <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="btn-ghost" style={{ padding: '6px 14px', fontSize: 13 }} onClick={onCerrar}>Listo</button>
      </div>
    </div>
  );
}

function PanelColumnas({ campos, visibles, esInicio, onCambio, onInicio, onCerrar }: {
  campos: Campo[]; visibles: string[]; esInicio: boolean; onCambio: (ks: string[]) => void; onInicio: () => void; onCerrar: () => void;
}) {
  const alternar = (k: string) => {
    const s = new Set(visibles); if (s.has(k)) s.delete(k); else s.add(k);
    onCambio(campos.filter((c) => s.has(c.clave)).map((c) => c.clave));
  };
  return (
    <div className="card fw-panel" data-panel="columnas" onKeyDown={(e) => { if (e.key === 'Escape' && !document.querySelector('[data-dialogo]')) onCerrar(); }}>
      <div style={{ gridColumn: '1 / -1', fontSize: 13 }} className="muted">
        Qué respuestas salen como columna. Lo demás se ve al abrir la fila. Se recuerda en este navegador para este formulario.
      </div>
      {campos.map((c) => (
        <label key={c.clave} data-columna={c.clave} style={{ alignItems: 'flex-start' }}>
          <input type="checkbox" checked={visibles.includes(c.clave)} onChange={() => alternar(c.clave)} style={{ marginTop: 3 }} />
          <span style={{ minWidth: 0 }}>{c.etiqueta}<span className="muted" style={{ display: 'block', fontSize: 11.5 }}>
            {TIPO_TXT[c.tipo]} · contestaron {c.respondidas} de {c.total}{c.constante ? ' · dice lo mismo en todas' : ''}</span></span>
        </label>
      ))}
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {!esInicio && <button type="button" className="btn-ghost" style={{ padding: '6px 14px', fontSize: 13 }} onClick={onInicio} data-columnas-inicio>Volver a las de inicio</button>}
        <button type="button" className="btn-ghost" style={{ padding: '6px 14px', fontSize: 13 }} onClick={onCerrar}>Listo</button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ analítica
const ejeCss = { fontSize: 11, fill: '#6b6b6b' };
function Tarjeta({ titulo, sub, children, campo }: { titulo: string; sub?: React.ReactNode; children: React.ReactNode; campo?: string }) {
  return (
    <div className="card" style={{ padding: '14px 16px', minWidth: 0 }} data-grafica={campo || titulo}>
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--gold-dark)' }}>{titulo}</h3>
      {sub && <div className="muted" style={{ fontSize: 12, margin: '2px 0 10px' }}>{sub}</div>}
      {children}
    </div>
  );
}
function Barras({ items, total, activos, onToca, etiqueta }: { items: { valor: string; n: number }[]; total: number; activos?: string[]; onToca?: (v: string) => void; etiqueta: string }) {
  const max = Math.max(1, ...items.map((i) => i.n));
  return (
    <div className="fw-barras" role="list" aria-label={etiqueta}>
      {items.map((i) => (
        <button key={i.valor} type="button" role="listitem" aria-pressed={activos ? activos.includes(i.valor) : undefined} disabled={!onToca} data-barra={i.valor}
          onClick={() => onToca?.(i.valor)} title={onToca ? `${i.valor}: ${i.n} (${pct(i.n, total)} %) — tocar para filtrar` : `${i.valor}: ${i.n} (${pct(i.n, total)} %)`}>
          <span className="et">{i.valor}</span><span className="val">{i.n} · {pct(i.n, total)} %</span>
          <span className="pista" aria-hidden="true"><i style={{ width: `${(i.n / max) * 100}%` }} /></span>
        </button>
      ))}
    </div>
  );
}
function Columnas({ datos, alto = 200, nombre }: { datos: { etiqueta: string; n: number }[]; alto?: number; nombre: string }) {
  return (
    <ResponsiveContainer width="100%" height={alto}>
      <BarChart data={datos} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="#eee7da" />
        <XAxis dataKey="etiqueta" tick={ejeCss} tickLine={false} axisLine={{ stroke: '#e5dccb' }} interval="preserveStartEnd" minTickGap={12} />
        <YAxis allowDecimals={false} tick={ejeCss} tickLine={false} axisLine={false} width={40} />
        <Tooltip cursor={{ fill: 'rgba(172,153,108,.12)' }} formatter={(v: number) => [v, nombre]} />
        <Bar dataKey="n" name={nombre} fill={ORO} radius={[4, 4, 0, 0]} maxBarSize={24} />
      </BarChart>
    </ResponsiveContainer>
  );
}
/** Conteo por día (o por mes si el rango pasa de ~3 meses), con los días sin nada en cero. */
function serieTiempo(dias: string[]): { etiqueta: string; n: number }[] {
  if (!dias.length) return [];
  const s = [...dias].sort(); const ini = s[0], fin = s[s.length - 1];
  const a = new Date(ini + 'T12:00:00Z'), b = new Date(fin + 'T12:00:00Z');
  const porMes = (b.getTime() - a.getTime()) / 864e5 > 92;
  const c = new Map<string, number>();
  for (const d of s) { const k = porMes ? d.slice(0, 7) : d; c.set(k, (c.get(k) || 0) + 1); }
  const out: { etiqueta: string; n: number }[] = [];
  const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  for (let x = new Date(a); x <= b; porMes ? x.setUTCMonth(x.getUTCMonth() + 1, 1) : x.setUTCDate(x.getUTCDate() + 1)) {
    const k = porMes ? x.toISOString().slice(0, 7) : x.toISOString().slice(0, 10);
    out.push({ etiqueta: porMes ? `${MESES[x.getUTCMonth()]} ${String(x.getUTCFullYear()).slice(2)}` : `${x.getUTCDate()} ${MESES[x.getUTCMonth()]}`, n: c.get(k) || 0 });
    if (out.length > 400) break;
  }
  return out;
}

function Analitica({ campos, filas, base, filtros, alternarValor }: { campos: Campo[]; filas: Registro[]; base: number; filtros: Filtros; alternarValor: (c: Campo, v: string) => void }) {
  const completos = filas.filter((r) => r.completo).length;
  const dias = filas.map((r) => (r.creado ? new Date(r.creado).toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' }) : '')).filter(Boolean);
  if (!filas.length) return <div className="card muted" style={{ padding: 24, textAlign: 'center', fontSize: 13.5 }}>Ninguna respuesta con estos filtros: no hay qué graficar.</div>;
  const tiles: [string, string, string?][] = [
    ['Respuestas', filas.length.toLocaleString('es-MX'), filas.length !== base ? `de ${base} sin filtros` : undefined],
    ['Completas', `${pct(completos, filas.length)} %`, `${completos} con nombre y teléfono o correo`],
    ['Campos', String(campos.length), `${campos.filter((c) => c.constante).length} dicen lo mismo en todas`],
  ];
  return (
    <div style={{ display: 'grid', gap: 12 }} data-analitica>
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 170px), 1fr))' }}>
        {tiles.map(([l, v, s]) => (
          <div key={l} className="card" style={{ padding: '10px 14px' }}><div className="muted" style={{ fontSize: 12 }}>{l}</div>
            <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--ink, #2d2a2e)' }}>{v}</div>{s && <div className="muted" style={{ fontSize: 11.5 }}>{s}</div>}</div>
        ))}
      </div>
      <Tarjeta titulo="Respuestas en el tiempo" sub="cuántas llegaron cada día (o cada mes, si el rango es largo)" campo="__tiempo">
        <Columnas datos={serieTiempo(dias)} nombre="Respuestas" />
      </Tarjeta>
      <div className="fw-graficas">
        {campos.map((c) => <GraficaCampo key={c.clave} c={c} filas={filas} filtro={filtros[c.clave]} alternarValor={alternarValor} />)}
      </div>
      <p className="muted" style={{ fontSize: 12, margin: 0 }}>Los porcentajes son sobre quienes contestaron cada pregunta. En las de varias opciones una persona cuenta en cada opción que eligió, así que suman más de 100 %. Tocar una barra filtra por ese valor (también la tabla).</p>
    </div>
  );
}

function GraficaCampo({ c, filas, filtro, alternarValor }: { c: Campo; filas: Registro[]; filtro?: Filtro; alternarValor: (c: Campo, v: string) => void }) {
  const vs = filas.map((r) => r.campos[c.clave]).filter((v) => !(v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)));
  const sub = <>{TIPO_TXT[c.tipo]} · contestaron {vs.length} de {filas.length} ({pct(vs.length, filas.length)} %)</>;
  if (!vs.length) return <Tarjeta titulo={c.etiqueta} sub={sub} campo={c.clave}><div className="muted" style={{ fontSize: 13 }}>Nadie la contestó con estos filtros.</div></Tarjeta>;
  if (c.constante) return <Tarjeta titulo={c.etiqueta} sub={sub} campo={c.clave}><div style={{ fontSize: 13 }}>Todas dicen «{c.valores[0]?.valor}».</div></Tarjeta>;
  const activos = filtro?.tipo === 'valores' ? filtro.valores : [];
  const contar = (lista: string[]) => { const m = new Map<string, number>(); for (const t of lista) if (t) m.set(t, (m.get(t) || 0) + 1); return [...m.entries()].map(([valor, n]) => ({ valor, n })).sort((a, b) => b.n - a.n || a.valor.localeCompare(b.valor, 'es')); };

  if (c.tipo === 'opcion' || c.tipo === 'si_no' || c.tipo === 'url' || c.tipo === 'multiple') {
    const items = contar(c.tipo === 'multiple' ? vs.flatMap((v) => elementos(c.clave, v)) : vs.map((v) => textoValor(c.clave, c.tipo, v)));
    return (
      <Tarjeta titulo={c.etiqueta} sub={sub} campo={c.clave}>
        <Barras items={items.slice(0, 12)} total={vs.length} activos={activos} onToca={(v) => alternarValor(c, v)} etiqueta={c.etiqueta} />
        {items.length > 12 && <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>+{items.length - 12} valores más</div>}
      </Tarjeta>
    );
  }
  if (c.tipo === 'numero') {
    const ns = vs.map(numero).filter((n): n is number => n !== null);
    const e = estadisticas(ns)!;
    const f = (x: number) => x.toLocaleString('es-MX', { maximumFractionDigits: 1 });
    return (
      <Tarjeta titulo={c.etiqueta} sub={sub} campo={c.clave}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13, marginBottom: 6 }} data-estadisticas>
          <span>Promedio <strong>{f(e.media)}</strong></span><span>Mediana <strong>{f(e.mediana)}</strong></span>
          <span className="muted">de {f(e.min)} a {f(e.max)}</span>
        </div>
        <Columnas datos={histograma(ns).map((h) => ({ etiqueta: h.etiqueta, n: h.n }))} alto={180} nombre="Respuestas" />
      </Tarjeta>
    );
  }
  if (c.tipo === 'fecha') {
    const ds = vs.map(fechaDia).filter((d): d is string => !!d);
    return <Tarjeta titulo={c.etiqueta} sub={sub} campo={c.clave}><Columnas datos={serieTiempo(ds)} alto={180} nombre="Respuestas" /></Tarjeta>;
  }
  // Texto: lo que más se repite; en texto largo, las palabras que más salen y unas respuestas de muestra.
  const textos = vs.map((v) => textoValor(c.clave, c.tipo, v));
  const repetidos = contar(textos).filter((x) => x.n > 1);
  if (c.tipo === 'texto_largo') {
    const ps = palabras(textos);
    return (
      <Tarjeta titulo={c.etiqueta} sub={sub} campo={c.clave}>
        {ps.length
          ? <><div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Palabras que más se repiten (en cuántas respuestas)</div><Barras items={ps} total={textos.length} etiqueta={`Palabras de ${c.etiqueta}`} /></>
          : <div className="muted" style={{ fontSize: 12.5 }}>Todavía ninguna palabra se repite entre respuestas.</div>}
        <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
          {textos.slice(0, 3).map((t, i) => <blockquote key={i} style={{ margin: 0, padding: '6px 10px', borderLeft: `3px solid ${ORO}`, background: '#fffdf8', fontSize: 12.5, overflowWrap: 'anywhere' }}>{t.length > 220 ? t.slice(0, 218) + '…' : t}</blockquote>)}
        </div>
      </Tarjeta>
    );
  }
  return (
    <Tarjeta titulo={c.etiqueta} sub={<>{sub} · {new Set(textos).size} distintas</>} campo={c.clave}>
      {repetidos.length
        ? <Barras items={repetidos.slice(0, 10)} total={vs.length} etiqueta={c.etiqueta} />
        : <div className="muted" style={{ fontSize: 13 }}>Cada quien contestó algo distinto: no hay valores que se repitan. Se ven en la tabla, al abrir la fila.</div>}
    </Tarjeta>
  );
}
